/**
 * The long-press radial ring. A long-press pops a ring of actions under the
 * finger. On clear water it's the open-water set (set destination, drop a mark,
 * make a note, start a route, read the wind). When the press lands ON a charted
 * object the ring adapts to that object: read its details, plot a transit through
 * it, hang a clearing bearing off it, make it the destination, plus the usual
 * spot actions, each seeded with the object's charted position and label. Tapping
 * the centre or the backdrop cancels, so an accidental press costs nothing.
 *
 * Layout note: the ring is clamped away from the screen edges so a press near a
 * corner still shows every item. Long-press timing/haptics are the prototype's
 * (380 ms, no haptic in this build) and flagged as an open tuning question.
 */
import { useShellStore } from '@/store/shell';
import { useRoutingStore, nextWaypointId } from '@/store/routing';
import { setDestinationAndOfferRoute } from './setDestinationFlow';
import { useChartPlotterStore } from '@/store';
import { useMeasureStore } from '@/store/measure';
import { useNotesStore, nextNoteId } from '@/store/notes';
import { usePilotageStore, nextClearingId } from '@/store/pilotage';
import { clearingSide, type ClearingBearing } from '@/pilotage';
import { rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import {
  RING_ACTIONS,
  OBJECT_RING_ACTIONS,
  ringItemOffset,
  type RingAction,
} from './ringActions';
import { haptic } from '@/ui/haptics';

const RADIUS = 92;
const MARGIN = RADIUS + 40; // keep the whole ring on screen

export function RadialRing() {
  const ring = useShellStore((s) => s.ring);
  const closeRing = useShellStore((s) => s.closeRing);
  const openSheet = useShellStore((s) => s.openSheet);
  const setEditingWaypoint = useShellStore((s) => s.setEditingWaypoint);
  const showToast = useShellStore((s) => s.showToast);
  const addMark = useRoutingStore((s) => s.addMark);
  const dropRouteWaypoint = useRoutingStore((s) => s.dropRouteWaypoint);
  const marks = useRoutingStore((s) => s.marks);

  // The long-press activation buzz is fired centrally by the map's long-press
  // handler (MapView), so the ring doesn't buzz again on open. Selecting a ring
  // item still buzzes in run() below.
  if (!ring) return null;

  // On a charted object we show the object ring and seed actions from the
  // object's charted position/label; on open water it's the default ring seeded
  // from the pressed point.
  const obj = ring.object ?? null;
  const actions = obj ? OBJECT_RING_ACTIONS : RING_ACTIONS;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const cx = Math.max(MARGIN, Math.min(vw - MARGIN, ring.x));
  const cy = Math.max(MARGIN, Math.min(vh - MARGIN, ring.y));

  function run(a: RingAction) {
    // Object actions work off the object's charted point and label; open-water
    // actions off the pressed point.
    const pos = obj ? obj.position : ring!.position;
    const label = obj?.label;
    // Committing actions (set the destination, drop a mark, start a route, plot a
    // transit or clearing bearing) get the firmer commit buzz; reading the wind,
    // measuring, reading what's here, or opening details is just a light tick.
    haptic(
      a.id === 'wind' ||
        a.id === 'tidehere' ||
        a.id === 'measure' ||
        a.id === 'whatshere' ||
        a.id === 'details'
        ? 'tick'
        : 'commit',
    );
    switch (a.id) {
      case 'destination':
        // Set the destination, then immediately offer to plot a route to it.
        setDestinationAndOfferRoute(pos, label);
        break;
      case 'mark': {
        // Drop the mark, then open its editor so we can choose the icon, name,
        // notes and position instead of being stuck with a default square. On an
        // object we name the mark after it.
        const id = nextWaypointId();
        addMark({ id, position: pos, name: label || `M${marks.length + 1}`, icon: 'plot-waypoint' });
        setEditingWaypoint({ type: 'mark', markId: id });
        openSheet('waypoint');
        break;
      }
      case 'makenote': {
        // Drop a note right where the ring was opened, then open its editor. We
        // already have the point, so there's no tap-to-place step.
        const id = nextNoteId();
        const now = Date.now();
        useNotesStore
          .getState()
          .addNote({ id, position: pos, title: label || undefined, text: '', createdMs: now, updatedMs: now });
        useShellStore.getState().setEditingNote(id);
        openSheet('note');
        break;
      }
      case 'measure':
        // Reuse the app's measure tool, seeded at the point; the ruler then grows
        // a leg per chart tap (distance + bearing + running total).
        useChartPlotterStore.getState().setActiveTool('measure');
        useMeasureStore.getState().start(pos);
        showToast('Ruler started — tap to add points');
        break;
      case 'route':
        dropRouteWaypoint(pos);
        showToast('Route started — long-press to add waypoints');
        break;
      case 'tidehere':
        // Straight to the tide sheet for this spot. setChartTap seeds the point
        // so the sheet reads heights for where the finger was, not for wherever
        // the boat happens to be.
        useShellStore.getState().setChartTap(pos, label ?? null);
        openSheet('tides');
        break;
      case 'whatshere':
        // A compact read of the spot: its position, charted depth (when the
        // source carries it), range/bearing from the boat, and the tide port we
        // read heights from here. The sheet computes the rest from this point.
        useShellStore.getState().setChartTap(pos, null);
        openSheet('whatshere');
        break;
      case 'wind':
        // Straight to the real forecast for this spot: several models, hour by
        // hour, day by day, with what they agree on. This used to pin one
        // synthetic barb and say so in a toast, which was all there was at the
        // time. Pinning is still available, as a button inside the sheet, because
        // an arrow on the chart is a different job from reading a forecast.
        useShellStore.getState().setChartTap(pos, null);
        openSheet('weathertable');
        break;
      // --- Object-ring actions ---
      case 'details':
        // Open the object's full detail sheet, exactly as a tap on it would.
        if (obj) {
          useChartPlotterStore.getState().setSelectedFeature(obj.feature, obj.siblings);
          openSheet('feature');
        }
        break;
      case 'transit':
        // Use this object as the BACK (rear) mark of a transit; the next chart
        // tap places the FRONT mark, snapping to a nearby object.
        if (obj) {
          useShellStore.getState().armTransitPick({ position: pos, label: label ?? 'Back mark' });
          useShellStore.getState().setMode('Plotting');
          showToast(`Back mark: ${label ?? 'object'} — now tap the front mark`);
        }
        break;
      case 'clearing':
        // Anchor a clearing bearing on this object. Default the bearing to the
        // object's current bearing from the boat and put the safe side on the
        // side the boat is on now; both adjustable after.
        if (obj) {
          const ownship = useChartPlotterStore.getState().ownship.position;
          const bearingDeg = ownship ? rhumbBearingDeg(ownship, pos) : 0;
          const lengthNm = ownship
            ? Math.max(0.1, Math.min(rhumbDistanceNm(ownship, pos), 3))
            : 0.5;
          const cb: ClearingBearing = {
            id: nextClearingId(),
            anchor: pos,
            anchorLabel: label,
            bearingDeg,
            lengthNm,
            safeSide: 'left',
            alarm: true,
            planId: null,
          };
          if (ownship) {
            const side = clearingSide(cb, ownship);
            if (side !== 'on') cb.safeSide = side;
          }
          usePilotageStore.getState().addClearing(cb);
          useShellStore.getState().setMode('Plotting');
          openSheet('clearing');
          showToast('Clearing bearing set — drag the free end to adjust');
        }
        break;
    }
    closeRing();
  }

  return (
    <div
      className="acp-ring-wrap"
      onClick={closeRing}
      role="dialog"
      aria-label={obj ? `${obj.label} — object actions` : 'Open-water actions'}
    >
      <div className="acp-ring" style={{ left: cx, top: cy }} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="acp-ring-centre" onClick={closeRing} aria-label="Cancel">
          ✕
        </button>
        {actions.map((a, i) => {
          const o = ringItemOffset(i, actions.length, RADIUS);
          return (
            <button
              key={a.id}
              type="button"
              className="acp-ring-item"
              style={{ transform: `translate(-50%, -50%) translate(${o.dx}px, ${o.dy}px)` }}
              onClick={() => run(a)}
              aria-label={a.label}
            >
              <span className="ico" aria-hidden>
                {GLYPH[a.glyph] ?? '•'}
              </span>
              <span className="lbl">{a.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Tiny unicode glyph fallbacks for the ring items (kept text-light). */
const GLYPH: Record<string, string> = {
  flag: '⚑',
  pin: '◎',
  note: '🗒️',
  measure: '📏',
  route: '⤳',
  wind: '≋',
  tide: '≈',
  info: 'ⓘ',
  transit: '⌖',
  clearing: '∡',
};
